import { UnfoldLess, UnfoldMore } from '@mui/icons-material';
import { Stack, Tooltip, Typography } from '@mui/material';
import { Handle, Position } from '@xyflow/react';
import { ResourceCard } from 'js/components/pipelines/cards/ResourceCard';
import {
  RESOURCE_TYPE_CREDENTIAL_GROUP,
  RESOURCE_TYPE_CREDENTIALS,
} from 'js/constants/resourceTypes';
import { ReactComponent as DetailsIcon } from 'new/components/icons/fi-rr-info.svg';
import { ReactComponent as CredentialGroupIcon } from 'new/components/icons/lock.svg';
import { useResourceDetailHandler } from 'new/components/ResourceDetailHandler/context';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';
import { IconButton } from 'new/elements/IconButton/IconButton';
import { useUsers } from 'new/hooks/adapters/useUsers';
import usePaging from 'new/state/paging/repository/usePaging';
import React, { useState } from 'react';
import styles from '../FlowNodes.module.scss';

interface CredentialGroupProps {
  data: {
    isPreview: boolean;
    credentialsData: any;
    height: number;
    width: number;
    key: string;
    nodeKey: string;
    [key: string]: any;
    isAdaptiveFlow: boolean;
  };
}

export const CredentialNode: React.FC<CredentialGroupProps> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const { credentialsData, isAdaptiveFlow, layoutDirection = 'LR' } = data;
  const users = useUsers(true);
  const { getMetaInfoById } = usePaging();
  const { openDetailDrawerInPortal } = useResourceDetailHandler();
  const connectionInfo = getMetaInfoById(credentialsData?.credentials_type);
  const ownerFullName =
    credentialsData?.owner?.full_name || users[credentialsData?.owner_id]?.full_name;

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const onOpenDetail = () => {
    openDetailDrawerInPortal(
      {
        ...credentialsData,
        type: isAdaptiveFlow ? RESOURCE_TYPE_CREDENTIAL_GROUP : RESOURCE_TYPE_CREDENTIALS,
      },
      connectionInfo,
    );
  };

  const isPreview = !!data?.isPreview;

  return (
    <div>
      <Handle
        type="source"
        position={layoutDirection === 'TB' ? Position.Right : Position.Top}
        isConnectable={false}
        className={styles.handle}
        id="credential-out"
      />
      {isAdaptiveFlow ? (
        <div
          className={styles.credentialGroupNode}
          style={{ height: data.height, width: data.width }}
        >
          <div className={styles.credentialGroupContent} style={{ cursor: 'pointer' }}>
            <div className={styles.credentialGroupHeader}>
              <Stack direction="row" gap="4px" alignItems="center">
                <ConnectionTypeChip
                  icon={<CredentialGroupIcon />}
                  style={{ border: 'none', padding: 0 }}
                  iconOnly
                />
                <Typography variant="subtitle2" className={styles.headerText}>
                  Credential Group
                </Typography>
              </Stack>
              <Stack direction="row">
                <IconButton size="small" onClick={handleToggleExpand}>
                  {expanded ? (
                    <UnfoldLess className={styles.collapseIcon} />
                  ) : (
                    <UnfoldMore className={styles.expandIcon} />
                  )}
                </IconButton>
                {!isPreview && (
                  <Tooltip title="Details">
                    <IconButton size="small" onClick={onOpenDetail}>
                      <DetailsIcon className={styles.detailIcon} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </div>
            {expanded && (
              <Stack gap="8px" className={styles.credentialList}>
                <span>
                  <b>Group Name:</b> {credentialsData?.name}
                </span>
                <span>
                  <b>Credentials count:</b> {credentialsData?.data_credentials_count}
                </span>
                <span>
                  <b>Group ID:</b> {credentialsData?.id}
                </span>
                <span>
                  <b>Owner:</b> {ownerFullName}
                </span>
              </Stack>
            )}
          </div>
        </div>
      ) : (
        <ResourceCard
          isPreview={isPreview}
          data={{
            ...credentialsData,
            resourceType: RESOURCE_TYPE_CREDENTIALS,
          }}
          shouldUseResourceIcon
          resourceType={RESOURCE_TYPE_CREDENTIALS}
          size="x-small"
          status="normal"
          connectionInfo={connectionInfo}
          toggleUseExpanded={() => {}}
        />
      )}
    </div>
  );
};
